import { Editor } from "@tinymce/tinymce-react";
import React, { ComponentProps, useState } from "react";
import { Editor as TinyMCEEditor } from "tinymce";
import "./font-wrapper.module.css";
import { PdfHeaderFooterModal } from "./pdf-options/pdf-header-footer-modal";

type VpdDesignEditorProps = {
  outputDesignId: number;
  templateString: string;
  handleEditorInit: (editor: TinyMCEEditor) => void;
  setDirty?: (dirty: boolean) => void;
  overrideOpts?: ComponentProps<typeof Editor>;
  removeTemplateMargin?: boolean;
  mergeTagFields?: { title: string; value: string | number }[];
};

export const VpdDesignEditor: React.FC<VpdDesignEditorProps> = ({
  outputDesignId,
  templateString,
  handleEditorInit,
  setDirty,
  overrideOpts,
  removeTemplateMargin,
  mergeTagFields,
}) => {
  const [showHeaderFooterModal, setShowHeaderFooterModal] = useState(false);

  const styles = `
@import url('https://fonts.googleapis.com/css2?family=Carlito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
body {
   padding: ${removeTemplateMargin ? "0px" : "30px 96px 30px 96px"};
   content_style: "body { font-family: Arial; }";
}
ol {
  margin: 0 !important;
  padding:0;
}
ol > li {
  position: relative;
  padding-left: 35px;
}
`;

  return (
    <>
      <PdfHeaderFooterModal
        outputDesignId={outputDesignId}
        open={showHeaderFooterModal}
        onClose={() => setShowHeaderFooterModal(false)}
        onOk={() => {
          setShowHeaderFooterModal(false);
        }}
      />
      <Editor
        tinymceScriptSrc={`https://assets.vpd.law/vpd-ui/tinymce/js/tinymce/tinymce.min.js`}
        scriptLoading={{
          async: true,
          defer: true,
        }}
        initialValue={templateString}
        onInit={(_, editor) => {
          handleEditorInit(editor);
        }}
        onDirty={() => setDirty && setDirty(true)}
        init={{
          license_key: undefined, // GPL does not work here.
          promotion: false,
          menu: {
            custom: { title: "PDF Options", items: "headerfooter joinPdfs" },
          },
          font_family_formats:
            "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Calibri=Carlito; Calibri Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
          verify_html: false,
          visualblocks_default_state: false,
          branding: false,
          style_formats_merge: true,
          end_container_on_empty_block: true,
          content_css: "document",
          adv_list_number_styles: "decimal",
          content_style: styles,
          indent_use_margin: true,
          table_sizing_mode: "fixed",
          table_use_colgroups: false,
          editable_root: true,
          editable_class: "editable",
          pagebreak_separator: `<div style="page-break-before:always">&nbsp;</div> `,
          newline_behavior: "invert",
          indent: false,
          lists_indent_on_tab: true,
          setup: function (ed) {
            ed.ui.registry.addAutocompleter("mergetags", {
              trigger: "{{",
              minChars: 0,
              columns: "auto",
              highlightOn: ["mergetags"],
              fetch: (pattern) => {
                return new Promise((resolve) => {
                  const matches =
                    mergeTagFields?.filter(
                      (field) =>
                        field.title.toLowerCase().includes(pattern.toLowerCase()) ||
                        field.value.toString().toLowerCase().includes(pattern.toLowerCase())
                    ) || [];

                  const results = matches.map((field) => ({
                    type: "cardmenuitem" as const,
                    value: `{{{${field.value}}}}`,
                    label: `${field.title} (${field.value})`,
                    items: [
                      {
                        type: "cardtext" as const,
                        direction: "vertical",
                        text: `${field.title} (${field.value})`,
                      },
                    ],
                  }));

                  resolve(results);
                });
              },
              onAction: (autocompleteApi, rng, value) => {
                ed.selection.setRng(rng);
                ed.insertContent(value);
                autocompleteApi.hide();
              },
            });

            ed.on("keydown", function (event) {
              if (event.key == "Tab") {
                ed.execCommand("Indent");
                event.preventDefault();
                return false;
              }
            });
            ed.ui.registry.addMenuItem("headerfooter", {
              text: "Header Footer",
              onAction: () => setShowHeaderFooterModal(true),
            });
          },
          height: "95%",
          width: "100%",
          resize: false,
          highlight_on_focus: false,
          menubar: "file edit view insert format tools help custom advancedPreview",
          plugins:
            "pagebreak nonbreaking directionality save anchor autolink charmap image link lists media searchreplace table visualblocks code",
          toolbar:
            "pagebreaknonbreaking directionality save undo redo | blocks fontfamily fontsize | bold italic underline strikethrough |  image table  | align lineheight | checklist numlist bullist indent outdent | charmap | removeformat",
          tinycomments_mode: "embedded",
          tinycomments_author: "Author name",

          ai_request: (
            request: any,
            respondWith: {
              string: (arg0: () => Promise<void>) => any;
            }
          ) => respondWith.string(() => Promise.reject("See docs to implement AI Assistant")),
        }}
        {...overrideOpts}
      />
    </>
  );
};
